import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import api from '../../services/api';

export default function SocialContactModal({ open, onClose, user }) {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [socialRequest, setSocialRequest] = useState('');
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    useEffect(() => {
        if (open && user) {
            setName(user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : '');
            setEmail(user.email || '');
        }
        if (!open) {
            setSent(false);
            setSocialRequest('');
            setNote('');
        }
    }, [open, user]);

    const handleSubmit = async () => {
        if (!name.trim() || !email.trim() || !socialRequest.trim()) {
            setSnack({ open: true, msg: t('social_contact.validation_error'), severity: 'error' });
            return;
        }
        setLoading(true);
        try {
            await api.post('/api/openai/social-contact/', {
                name: name.trim(),
                email: email.trim(),
                social_request: socialRequest.trim(),
                note: note.trim(),
            });
            setSent(true);
        } catch {
            setSnack({ open: true, msg: t('social_contact.send_error'), severity: 'error' });
        }
        setLoading(false);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: '#ffffff',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    }
                }}
            >
                <DialogTitle sx={{ p: 3, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '12px',
                            background: 'linear-gradient(135deg, #701280, #9B59B6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <ShareIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
                                {t('social_contact.title')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                                {t('social_contact.subtitle')}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: '#9CA3AF' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 3, py: 2 }}>
                    {sent ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Box sx={{ fontSize: '3rem', mb: 2 }}>✅</Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827', mb: 1 }}>
                                {t('social_contact.success_title')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: '#6B7280' }}>
                                {t('social_contact.success_desc')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Typography sx={{ fontSize: '0.83rem', color: '#4B5563', lineHeight: 1.6 }}>
                                {t('social_contact.intro')}
                            </Typography>

                            <TextField
                                label={t('social_contact.field_name')}
                                value={name}
                                onChange={e => setName(e.target.value)}
                                size="small"
                                fullWidth
                                required
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                            <TextField
                                label={t('social_contact.field_email')}
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                size="small"
                                fullWidth
                                required
                                type="email"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                            <TextField
                                label={t('social_contact.field_socials')}
                                value={socialRequest}
                                onChange={e => setSocialRequest(e.target.value)}
                                size="small"
                                fullWidth
                                required
                                multiline
                                rows={2}
                                placeholder={t('social_contact.field_socials_placeholder')}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                            <TextField
                                label={t('social_contact.field_note')}
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                placeholder={t('social_contact.field_note_placeholder')}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
                    {sent ? (
                        <Button
                            fullWidth
                            onClick={onClose}
                            variant="contained"
                            sx={{
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #701280, #9B59B6)',
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1.1,
                            }}
                        >
                            {t('social_contact.close_btn')}
                        </Button>
                    ) : (
                        <>
                            <Button
                                onClick={onClose}
                                sx={{ borderRadius: '10px', textTransform: 'none', color: '#6B7280', fontWeight: 500 }}
                            >
                                {t('social_contact.cancel_btn')}
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={loading || !name.trim() || !email.trim() || !socialRequest.trim()}
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                                sx={{
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #701280, #9B59B6)',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1.1,
                                    '&:hover': { background: 'linear-gradient(135deg, #5a0f69, #7d45a0)' },
                                    '&.Mui-disabled': { background: '#E5E7EB', color: '#9CA3AF' },
                                }}
                            >
                                {loading ? t('social_contact.sending') : t('social_contact.send_btn')}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </>
    );
}
